import {
    _parseString,
    _yes,
    _no,
    _indeterminate,
    _undecidable,
    _pure,
    _evaluate,
    _freeTerms,
    _toString,
    _bind,
    _nodeType,
    _children,
    _precedence,
    _conj,
    _foldConj,
    _disj,
    _foldDisj,
    _not,
    _implies,
    _equiv,
    _isTerm,
    _isValue,
    _elimClassicalValues,
    _dnf,
    _cnf,
} from '../../../../../dist/libs/gino/multi-valued-logic/purescript/output/Public.Expr/index.js';
import type { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';

export type NormalForm = Record<string, boolean>[];

export type B4ExprProp =
    | B4Expr
    | string
    | number
    | boolean
    | undefined
    | null
    | PredicateValue;

export type B4ExprInput = B4ExprProp | (() => B4ExprProp);
export type B4ExprSubstitutions =
    | Record<string, B4ExprInput>
    | ((term: string) => B4ExprInput)
    | Iterable<{ term: string; binds: B4ExprInput } | [string, B4ExprInput]>
    | { term: string; binds: B4ExprInput }
    | B4ExprInput;

export type B4ExprNodeType =
    | 'Leaf'
    | 'Neg'
    | 'Implies'
    | 'Equiv'
    | 'And'
    | 'Or';
export type B4Precedence = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Public facade for predicate expressions.
 */
export class B4Expr {
    private _ast: unknown;

    constructor(ast: unknown) {
        this._ast = ast;
    }

    static YES: B4Expr;
    static NO: B4Expr;
    static UNDECIDABLE: B4Expr;
    static INDETERMINATE: B4Expr;

    static from(value: B4ExprInput): B4Expr {
        if (typeof value === 'string') {
            return new B4Expr(_parseString(value)());
        }

        if (value === true) return this.YES;
        if (value === false) return this.NO;
        if (value === null) return this.UNDECIDABLE;
        if (value === undefined) return this.INDETERMINATE;

        if (typeof value === 'number') {
            if (value > 0) return this.YES;
            if (value <= 0) return this.NO;
            return this.UNDECIDABLE;
        }

        if (value instanceof B4Expr) {
            return value;
        }

        if (typeof value === 'function') {
            return this.from(value());
        }

        return this.UNDECIDABLE;
    }

    static pure(term: string) {
        return new B4Expr(_pure(term));
    }

    static term(term: string) {
        return B4Expr.pure(term);
    }

    get nodeType(): B4ExprNodeType {
        return _nodeType(this._ast);
    }

    get children(): B4Expr[] {
        return _children(this._ast).map((ast) => new B4Expr(ast));
    }

    get precedence(): B4Precedence {
        return _precedence(this._ast);
    }

    static and(...inputs: B4ExprInput[]): B4Expr {
        return new B4Expr(_foldConj(inputs.map((i) => B4Expr.from(i)._ast)));
    }

    and(...others: B4ExprInput[]): B4Expr {
        if (others.length === 0) return this;
        return new B4Expr(_conj(this._ast)(B4Expr.and(...others)._ast));
    }

    static or(...inputs: B4ExprInput[]): B4Expr {
        return new B4Expr(_foldDisj(inputs.map((i) => B4Expr.from(i)._ast)));
    }

    or(...others: B4ExprInput[]): B4Expr {
        if (others.length === 0) return this;
        return new B4Expr(_disj(this._ast)(B4Expr.or(...others)._ast));
    }

    static implies(lhs: B4ExprInput, rhs: B4ExprInput): B4Expr {
        return B4Expr.from(lhs).implies(rhs);
    }

    implies(other: B4ExprInput): B4Expr {
        return new B4Expr(_implies(this._ast)(B4Expr.from(other)._ast));
    }

    static impliedBy(lhs: B4ExprInput, rhs: B4ExprInput): B4Expr {
        return B4Expr.from(lhs).impliedBy(rhs);
    }

    impliedBy(other: B4ExprInput): B4Expr {
        return new B4Expr(_implies(B4Expr.from(other)._ast)(this._ast));
    }

    static equiv(lhs: B4ExprInput, rhs: B4ExprInput): B4Expr {
        return B4Expr.from(lhs).equiv(rhs);
    }

    equiv(other: B4ExprInput): B4Expr {
        return new B4Expr(_equiv(this._ast)(B4Expr.from(other)._ast));
    }

    static neg(expr: B4ExprInput): B4Expr {
        return B4Expr.from(expr).neg();
    }

    neg(): B4Expr {
        return new B4Expr(_not(this._ast));
    }

    static not(expr: B4ExprInput): B4Expr {
        return B4Expr.from(expr).not();
    }

    not(): B4Expr {
        return this.neg();
    }

    isTautology(): boolean {
        return this.cnf().length == 0;
    }

    isContradiction(): boolean {
        return this.dnf().length == 0;
    }

    get evaluated(): PredicateValue {
        return _evaluate(this._ast) as unknown as PredicateValue;
    }

    get terms(): string[] {
        return _freeTerms(this._ast);
    }

    isLeaf(): boolean {
        return this.nodeType === 'Leaf';
    }

    isTerm(): boolean {
        return _isTerm(this._ast);
    }

    isValue(): boolean {
        return _isValue(this._ast);
    }

    elimClassicalValues(): B4Expr {
        return new B4Expr(_elimClassicalValues(this._ast));
    }

    toString(): string {
        return _toString(this._ast);
    }

    dnf(): NormalForm {
        return _dnf(this._ast);
    }

    cnf(): NormalForm {
        return _cnf(this._ast);
    }

    [Symbol.toPrimitive](hint: string) {
        if (hint === 'string') {
            return this.toString();
        }

        if (hint === 'number') {
            return NaN;
        }

        return true;
    }

    get [Symbol.toStringTag](): string {
        const base = 'B4Expr.' + this.nodeType;
        if (this.isLeaf()) {
            return base + '.' + (this.isTerm() ? 'Term' : 'Value');
        } else {
            return base;
        }
    }

    _bindFn(fn: any) {
        return new B4Expr(
            _bind(this._ast)((term) => {
                const result = fn(term);

                if (result === null || result === undefined)
                    return B4Expr.pure(term)._ast;
                return B4Expr.from(result)._ast;
            })
        );
    }

    _bindIterable(binds: any) {
        const map = new Map();

        for (const bind of binds) {
            let key: any, value: any;
            if (Array.isArray(bind) && bind.length == 2) {
                key = bind[0];
                value = bind[1];
            } else {
                const { term, binds } = bind;
                key = term;
                value = binds;
            }

            if (
                !(typeof key === 'string') ||
                value === null ||
                value === undefined
            )
                continue;

            map.set(key, B4Expr.from(value)._ast);
        }

        return new B4Expr(
            _bind(this._ast)(
                (term: any) => map.get(term) ?? B4Expr.pure(term)._ast
            )
        );
    }

    bind(fn: B4ExprSubstitutions): B4Expr {
        if (fn === null || fn === undefined) return new B4Expr(this._ast);

        if (typeof fn === 'function') {
            return this._bindFn(fn);
        }

        if (
            typeof fn === 'string' ||
            typeof fn === 'boolean' ||
            typeof fn === 'number'
        ) {
            return this._bindFn(() => fn);
        }

        if (typeof fn === 'object') {
            if (fn instanceof B4Expr) return this._bindFn(() => fn);
            if (typeof fn[Symbol.iterator] === 'function')
                return this._bindIterable(fn);

            if ('term' in fn && typeof fn.term === 'string' && 'binds' in fn) {
                return this._bindIterable([fn]);
            }

            return this._bindIterable(Object.entries(fn));
        }

        throw new Error('Invalid bind argument');
    }
}

B4Expr.YES = new B4Expr(_yes);
B4Expr.NO = new B4Expr(_no);
B4Expr.INDETERMINATE = new B4Expr(_indeterminate);
B4Expr.UNDECIDABLE = new B4Expr(_undecidable);

export default B4Expr;
